import React, { useState } from "react";
import ReactTable from "react-table";
import { useTranslation } from "react-i18next";
import range from "lodash/range";

import { translations } from "helpers/react-table";

const getPageSizeOptions = (total) =>
  [10, 15].concat(
    range(7)
      .map((i) => i * 5 + 20)
      .filter((n) => n <= total),
  );

const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatQuality = (value) => {
  if (
    String(value) === "-9223372036854775808" ||
    value === -9223372036854776000
  ) {
    return "-";
  }
  return `${value * 100}%` || "-";
};

const EyeCell = () => (
  <div
    css={{
      textAlign: "center",
      opacity: 0.7,
      "&:hover": { opacity: 1 },
    }}
  >
    <i className="icon-eye" />
  </div>
);

export const DataManagementTable = ({
  meters,
  isFetching,
  onSelectionChange,
}) => {
  const { t } = useTranslation();
  const ref = React.useRef(null);
  const [selectedRows, setSelectedRows] = useState({});

  React.useEffect(() => {
    if (onSelectionChange) {
      const selectedIds = Object.keys(selectedRows).filter(
        (k) => selectedRows[k],
      );
      onSelectionChange(selectedIds);
    }
  }, [selectedRows, onSelectionChange]);

  const formatTimestep = (timestep) => {
    if (timestep === null || timestep === undefined) return "";

    switch (timestep) {
      case 0:
        return t("global.step.inconnu");
      case 1:
        return t("global.step.yearly");
      case 2:
        return t("global.step.quarterly");
      case 3:
        return t("global.step.monthly");
      case 4:
        return t("global.step.daily");
      case 5:
        return t("global.step.hourly");
      case 6:
        return t("global.step.30mins");
      case 7:
        return t("global.step.15mins");
      case 8:
        return t("global.step.10mins");
      case 11:
        return t("global.step.5mins");
      default:
        return "";
    }
  };

  const data = React.useMemo(
    () =>
      !meters || meters.length === 0
        ? []
        : Object.values(meters).map((meter) => ({
            id: meter.Id,
            deliveryPoint: meter.PeeClef,
            energie: meter.PeeNrj || "-",
            site: meter.PeeSphNom || "-",
            code: meter.PeeClef || "-",
            country: meter.LibellePays || "-",
            grd: meter.NomReseau || "-",
            startReel: meter.StartReel || "-",
            endReel: meter.EndReel || "-",
            timestepReel: meter.TimestepReel || "-",
            qualityReel: formatQuality(meter.QualityReel),
            startPrevu: meter.StartPrevu || "-",
            endPrevu: meter.EndPrevu || "-",
            timestepPrevu: meter.TimestepPrevu || "-",
            qualityPrevu: formatQuality(meter.QualityPrevu),
          })),
    [meters],
  );

  const toggleRow = (id) => {
    setSelectedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const toggleAll = () => {
    const isAllSelected =
      Object.keys(selectedRows).filter((k) => selectedRows[k]).length ===
      data.length;

    const newSelectedRows = {};
    if (!isAllSelected) {
      data.forEach((row) => {
        newSelectedRows[row.deliveryPoint] = true;
      });
    }
    setSelectedRows(newSelectedRows);
  };

  const isAllSelected =
    data.length > 0 &&
    Object.keys(selectedRows).filter((k) => selectedRows[k]).length ===
      data.length;

  const columns = React.useMemo(
    () =>
      [
        {
          id: "checkbox",
          accessor: "",
          Cell: ({ original }) => {
            return (
              <input
                type="checkbox"
                className="checkbox"
                checked={selectedRows[original.deliveryPoint] === true}
                onChange={() => toggleRow(original.deliveryPoint)}
                onClick={(e) => e.stopPropagation()}
              />
            );
          },
          Header: () => {
            return (
              <input
                type="checkbox"
                className="checkbox"
                checked={isAllSelected}
                onChange={toggleAll}
              />
            );
          },
          sortable: false,
          filterable: false,
          width: 45,
        },
        {
          Header: t("dataManagement.table.deliveryPoint"),
          accessor: "deliveryPoint",
        },
        {
          Header: t("dataManagement.table.energie"),
          accessor: "energie",
        },
        {
          Header: t("dataManagement.table.site"),
          accessor: "site",
        },
        {
          Header: t("dataManagement.table.code"),
          accessor: "code",
        },
        {
          Header: t("dataManagement.table.country"),
          accessor: "country",
        },
        {
          Header: t("dataManagement.table.grd"),
          accessor: "grd",
        },
        {
          Header: t("dataManagement.table.startReel"),
          accessor: "startReel",
          Cell: ({ value }) => formatDate(value),
        },
        {
          Header: t("dataManagement.table.endReel"),
          accessor: "endReel",
          Cell: ({ value }) => formatDate(value),
        },
        {
          Header: t("dataManagement.table.timestepReel"),
          accessor: "timestepReel",
          Cell: ({ value }) => formatTimestep(value),
        },
        {
          Header: t("dataManagement.table.qualityReel"),
          accessor: "qualityReel",
        },
        {
          Header: t("dataManagement.table.startPrevu"),
          accessor: "startPrevu",
          Cell: ({ value }) => formatDate(value),
        },
        {
          Header: t("dataManagement.table.endPrevu"),
          accessor: "endPrevu",
          Cell: ({ value }) => formatDate(value),
        },
        {
          Header: t("dataManagement.table.timestepPrevu"),
          accessor: "timestepPrevu",
          Cell: ({ value }) => formatTimestep(value),
        },
        {
          Header: t("dataManagement.table.qualityPrevu"),
          accessor: "qualityPrevu",
        },
      ].map((column) =>
        column.filterable === false
          ? column
          : {
              ...column,
            },
      ),
    [t, selectedRows, data, isAllSelected],
  );

  return (
    <>
      <ReactTable
        ref={ref}
        data={data}
        columns={columns}
        defaultPageSize={15}
        loading={isFetching}
        pageSizeOptions={getPageSizeOptions(data.length)}
        getTrProps={(_, rowInfo) => ({
          style: { cursor: rowInfo ? "pointer" : "unset" },
        })}
        {...translations(t)}
        className="shadow-sm -striped -highlight"
        css={{
          fontSize: 13,
          ".rt-thead.-filters .rt-th": {
            overflow: "unset",
          },
          ".-selected": {
            backgroundColor: "rgba(0, 123, 255, 0.1) !important",
          },
        }}
        filterable
      />
    </>
  );
};
